// Rich marketing sections for the festival page (Madruga flagship). Content is
// data-driven (see lib/festival-content); all styling uses the venue theme
// tokens so real brand assets reskin it with zero markup changes. Fully
// responsive: single column on mobile, multi-column grids on wider screens.
import { useEffect, useRef, useState } from "react";
import type { FestivalHero, FestivalStoryContent } from "../lib/festival-content";
import { joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { PhoneInput } from "../components/phone-input";

// Minimal centred landing: the festival logo + a presale mailing-list signup
// (email → MailingListSubscriber for this venue), vertically centred over the
// page backdrop. Used when content.splash is set.
export function FestivalSplash({
  logo,
  name,
  venueId,
  ctaLabel,
  dates,
  heading,
  subline,
  listId,
}: {
  logo?: string;
  name: string;
  venueId?: string;
  ctaLabel: string;
  dates?: string;
  heading?: string;
  subline?: string;
  // Which mailing list (MailingList id) new sign-ups join. Unset → the venue's
  // default list. `source` stays "festival_presale" for analytics.
  listId?: string;
}) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // optional E.164, from PhoneInput
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!venueId || !email.trim() || status === "loading") return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setStatus("error");
      setError("Enter a valid email address");
      return;
    }
    setStatus("loading");
    setError(null);
    try {
      await joinMailingList(venueId, email.trim(), "festival_presale", phone || undefined, listId || undefined);
      pushFormInteraction("success");
      setStatus("done");
    } catch (err) {
      pushFormInteraction("fail");
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong, try again");
    }
  }

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center gap-10 px-4 py-20 text-center">
      {logo ? (
        <img
          src={logo}
          alt={name}
          className="festival-logo-pulse w-72 max-w-[78vw] object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] md:w-[26rem]"
        />
      ) : (
        <h1 className="text-5xl font-black tracking-tight text-white drop-shadow-lg md:text-7xl">{name}</h1>
      )}

      {status === "done" ? (
        <p className="max-w-sm text-lg text-white drop-shadow">
          You’re on the list. We’ll email you the moment presale tickets go live.
        </p>
      ) : (
        <div className="w-full max-w-md">
          {dates && (
            <p className="text-2xl font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] md:text-3xl">
              {dates}
            </p>
          )}
          {heading && (
            <h2 className="text-2xl font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] md:text-3xl">
              {heading}
            </h2>
          )}
          {subline && (
            <p className="mt-2 text-sm text-white/80 drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)] md:text-base">
              {subline}
            </p>
          )}

          {/* Minimal: one input + one CTA, pill shapes, glassy input, a soft
              amber glow on the CTA. Inline styles (not Tailwind classes) for the
              pill shape/border/padding because the global unlayered input/button
              base styles (border-radius:0 etc.) would otherwise win. */}
          <form onSubmit={submit} noValidate className="mt-6 flex flex-col gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              className="w-full min-w-0 placeholder-white/45 outline-none backdrop-blur-md"
              style={{
                height: "3.25rem",
                minHeight: "3.25rem",
                boxSizing: "border-box",
                flexShrink: 0,
                borderRadius: "13px",
                border: "1px solid rgba(255,255,255,0.28)",
                background: "rgba(0,0,0,0.4)",
                padding: "0 1.25rem",
                fontSize: "1rem",
                color: "#fff",
              }}
            />
            {/* Optional phone, industry-standard country-code + number → E.164. */}
            <PhoneInput onChange={setPhone} disabled={status === "loading"} />
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full transition hover:brightness-105 disabled:opacity-60"
              style={{
                height: "3.25rem",
                minHeight: "3.25rem",
                boxSizing: "border-box",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "13px",
                border: "none",
                padding: "0 1.9rem",
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                backgroundColor: "var(--accent)",
                color: "var(--accent-fg)",
                boxShadow: "0 14px 40px -12px var(--accent)",
              }}
            >
              {status === "loading" ? "…" : ctaLabel}
            </button>
          </form>

          {error && <p className="mt-3 text-sm text-red-300 drop-shadow">{error}</p>}
        </div>
      )}
    </div>
  );
}

// Injects @font-face for an admin-uploaded festival font (as "FestivalDisplay")
// so theme.displayFont can reference it. Bundled fonts (Alte Haas Grotesk) don't
// need this, they're declared in globals.css. A single injection anywhere in
// the DOM defines the face document-wide.
export function FestivalFontFace({ url }: { url?: string }) {
  if (!url) return null;
  const fmt = url.endsWith(".woff2")
    ? "woff2"
    : url.endsWith(".woff")
      ? "woff"
      : url.endsWith(".otf")
        ? "opentype"
        : "truetype";
  return (
    <style>{`@font-face{font-family:"FestivalDisplay";src:url("${url}") format("${fmt}");font-display:swap;}`}</style>
  );
}

// Page-wide backdrop behind all festival sections. A big atmospheric image
// (cover + a top/bottom darkening gradient so the photo shows through the
// middle while nav/footer/text stay legible) when provided, otherwise a subtle
// themed glow over the app background. Fixed + behind content + non-interactive.
export function FestivalBackdrop({
  image,
  imagePortrait,
  accent,
}: {
  image?: string;
  imagePortrait?: string;
  accent?: string;
}) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {image ? (
        <>
          {/* Art direction: a tall portrait crop on narrow (mobile) screens so
              the backdrop fills the frame; the wide landscape shot otherwise. */}
          <picture>
            {imagePortrait && (
              <source media="(max-width: 640px)" srcSet={imagePortrait} />
            )}
            <img
              src={image}
              alt=""
              className="h-full w-full object-cover"
              decoding="async"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          {/* Darken top + bottom for nav/footer/text legibility, let the photo
              show through the middle. */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/40 to-black/85" />
        </>
      ) : (
        <div
          className="h-full w-full"
          style={{
            background: `radial-gradient(120% 80% at 50% -10%, ${accent ?? "var(--accent)"}22, transparent 60%), var(--background)`,
          }}
        />
      )}
    </div>
  );
}

// Big, full-height festival hero. Renders a background VIDEO (muted/looped/
// autoplay, poster fallback, paused under prefers-reduced-motion) or a big
// background IMAGE (eager, high fetch-priority), with a dark gradient for text
// legibility and either the wordmark logo or the title overlaid. Fixed min-
// heights + object-cover keep it sharp and shift-free on web + mobile.
export function FestivalHeroSection({
  hero,
  fallbackImage,
  title,
  dateLine,
  strapline,
}: {
  hero?: FestivalHero;
  fallbackImage?: string | null;
  title: string;
  dateLine: string;
  strapline?: string;
}) {
  const image = hero?.image ?? fallbackImage ?? null;
  const video = hero?.video ?? null;
  const poster = hero?.poster ?? image ?? undefined;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const on = () => setReduceMotion(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduceMotion) v.pause();
    else void v.play().catch(() => {});
  }, [reduceMotion, video]);

  return (
    <section className="relative min-h-[68vh] w-full overflow-hidden rounded-[var(--radius)] md:min-h-[74vh] lg:min-h-[82vh]">
      {video && !reduceMotion ? (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
        >
          <source src={video} />
        </video>
      ) : image ? (
        <img
          src={image}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          // hero is above the fold → load it first
          fetchPriority="high"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--surface-2)]" />
      )}

      {/* Legibility gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />

      <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
        {hero?.logo ? (
          <img
            src={hero.logo}
            alt={hero.logoAlt ?? title}
            className="mb-3 h-16 w-auto max-w-[80%] object-contain md:h-24"
          />
        ) : (
          <h1 className="text-5xl font-black leading-[0.95] tracking-tight text-white md:text-7xl">
            {title}
          </h1>
        )}
        <p className="mt-3 text-base text-white/85 md:text-lg">{dateLine}</p>
        {strapline && (
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/70 md:text-base">{strapline}</p>
        )}
      </div>
    </section>
  );
}



function SectionHeading({ n, title, sub }: { n: string; title: string; sub?: string }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <span className="text-sm font-black uppercase tracking-[0.2em] text-[var(--accent)]">{n}</span>
      <div>
        <h2 className="text-2xl font-black leading-tight text-[var(--foreground)] md:text-3xl">{title}</h2>
        {sub && <p className="mt-1 text-sm text-[var(--muted)]">{sub}</p>}
      </div>
    </div>
  );
}

// Marketing block shown ABOVE the ticketing steps: concept, what-to-expect,
// lineup. `only` renders a single section (for the standalone pages).
export function FestivalStoryTop({
  content,
  only,
}: {
  content: FestivalStoryContent;
  only?: "expect" | "lineup";
}) {
  const show = (s: "intro" | "expect" | "lineup") => !only || only === s;
  return (
    <div className="space-y-12">
      {show("intro") && content.intro && (
        <section>
          <p className="max-w-2xl text-lg leading-relaxed text-[var(--foreground)]/85 md:text-xl">
            {content.intro}
          </p>
        </section>
      )}

      {show("expect") && content.expect && content.expect.length > 0 && (
        <section id="expect" className="scroll-mt-24">
          <SectionHeading n="01" title="What to expect" sub="More than a lineup" />
          <div className="space-y-6">
            {content.expect.map((group) => (
              <div key={group.heading}>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[var(--foreground)]/60">
                  {group.heading}
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {group.features.map((f) => (
                    <div
                      key={f.title}
                      className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4"
                    >
                      {f.icon && <div className="text-2xl">{f.icon}</div>}
                      <p className="mt-2 text-sm font-semibold leading-snug text-[var(--foreground)]">{f.title}</p>
                      {f.body && <p className="mt-1 text-xs text-[var(--muted)]">{f.body}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {show("lineup") && content.lineup && (content.lineup.headliners?.length || content.lineup.artists?.length) && (
        <section id="lineup" className="scroll-mt-24">
          <SectionHeading n="02" title="Lineup" sub={content.lineup.note} />
          {content.lineup.headliners && content.lineup.headliners.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {content.lineup.headliners.map((h) => (
                <span
                  key={h}
                  className="rounded-full px-4 py-1.5 text-base font-black lowercase"
                  style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                >
                  {h}
                </span>
              ))}
            </div>
          )}
          {content.lineup.artists && content.lineup.artists.length > 0 && (
            <p className="max-w-3xl text-base leading-relaxed text-[var(--foreground)]/70">
              {content.lineup.artists.join(" · ")}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

// Info block shown BELOW the checkout: getting there, FAQ, gallery.
export function FestivalStoryBottom({ content }: { content: FestivalStoryContent }) {
  return (
    <div id="info" className="scroll-mt-24 space-y-12">
      {content.travel && content.travel.length > 0 && (
        <section>
          <SectionHeading n="03" title="Getting there" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {content.travel.map((t) => (
              <div
                key={t.mode}
                className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4"
              >
                <p className="font-semibold text-[var(--foreground)]">{t.mode}</p>
                <p className="mt-1 text-sm text-[var(--muted)]">{t.detail}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {content.faq && content.faq.length > 0 && (
        <section>
          <SectionHeading n="04" title="Need to know" />
          <div className="divide-y divide-[var(--border-default)] overflow-hidden rounded-[var(--radius)] border border-[var(--border-default)]">
            {content.faq.map((f) => (
              <details key={f.q} className="group bg-[var(--surface)] p-4 [&_summary]:cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-[var(--foreground)] marker:content-['']">
                  {f.q}
                  <span className="ml-3 text-[var(--muted)] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-[var(--foreground)]/75">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {content.gallery && content.gallery.length > 0 && (
        <section>
          <SectionHeading n="05" title="Gallery" />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {content.gallery.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`Festival photo ${i + 1}`}
                loading="lazy"
                className="aspect-square w-full rounded-[var(--radius)] object-cover"
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
